import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as ViewIcon,
  FileDownload as FileDownloadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { TimelineItem } from '../TicketComponents/TimelineItem';
import { StatusChip } from '../TicketComponents/StatusChip';
import { PriorityChip } from '../TicketComponents/PriorityChip';
import { DialogWrapper } from '../DialogWrapper';
import { useEscKey } from '../../../hooks/useEscKey';

interface DetailViewDialogProps {
  open: boolean;
  onClose: () => void;
  ticket: any;
  authUser: any;
  modules: any[];
  priorities: any[];
  ticketFiles: any[];
  filesLoading: boolean;
  onFilePreview: (file: any) => void;
  onFileDownload: (file: any) => void;
  onEditTicketOpen: (ticket: any) => void;
  onEdit: (ticket: any) => void;
}

export const DetailViewDialog: React.FC<DetailViewDialogProps> = ({
  open,
  onClose,
  ticket,
  authUser,
  modules,
  priorities,
  ticketFiles,
  filesLoading,
  onFilePreview,
  onFileDownload,
  onEditTicketOpen,
  onEdit,
}) => {
  useEscKey(open, onClose);
  if (!ticket) return null;

  const decodeFileName = (fileName: string): string => {
    try {
      if (!fileName) return 'dosya';
      
      try {
        if (fileName.match(/^[A-Za-z0-9+/]+={0,2}$/) && fileName.length % 4 === 0) {
          const decoded = atob(fileName);
          if (decoded) return decoded;
        }
      } catch (e) {}

      try {
        const decoded = decodeURIComponent(escape(fileName));
        if (decoded !== fileName) return decoded;
      } catch (e) {}

      let fixedName = fileName
        .replace(/Ã/g, 'Ç').replace(/Ã§/g, 'ç')
        .replace(/Ä/g, 'Ğ').replace(/Ä/g, 'ğ')
        .replace(/Ã/g, 'Ü').replace(/Ã¼/g, 'ü')
        .replace(/Å/g, 'Ş').replace(/Å/g, 'ş')
        .replace(/Ã/g, 'İ').replace(/Ã¶/g, 'ö')
        .replace(/Ã/g, 'Ö').replace(/Ã½/g, 'ı')
        .replace(/Ã/g, 'I').replace(/Ã°/g, 'İ');

      if (/[ÃÅ]/.test(fixedName)) {
        fixedName = fixedName
          .replace(/[^\x00-\x7F]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      return fixedName || 'dosya';
    } catch (error) {
      return fileName || 'dosya';
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const fileType = getFileType(fileName);
    
    switch (fileType) {
      case 'pdf': return '📄';
      case 'image': return '🖼️';
      case 'office': 
        if (['doc', 'docx'].includes(ext || '')) return '📝';
        if (['xls', 'xlsx'].includes(ext || '')) return '📊';
        if (['ppt', 'pptx'].includes(ext || '')) return '📽️';
        return '📎';
      case 'text': return '📃';
      default: return '📎';
    }
  };

  const getFileType = (fileName: string): 'image' | 'pdf' | 'office' | 'text' | 'other' => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext || '')) {
      return 'image';
    } else if (ext === 'pdf') {
      return 'pdf';
    } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext || '')) {
      return 'office';
    } else if (['txt', 'csv'].includes(ext || '')) {
      return 'text';
    } else {
      return 'other';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const InfoRow = ({ label, value }: { label: string; value: any }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #f0f0f0' }}>
      <Typography variant="body2" fontWeight="bold" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2">
        {value}
      </Typography>
    </Box>
  );

  const canEdit =
    (authUser?.role === 'customer' && ticket.created_by === authUser?.id) ||
    (authUser?.role === 'admin') ||
    (authUser?.role === 'support' && ticket.assigned_to === authUser?.id);

  return (
    <DialogWrapper 
      open={open} 
      onClose={onClose}
      maxWidth="lg" 
      fullWidth
      disableEscapeKeyDown={true}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              💼 Talep Detayları
            </Typography>
            <Chip label={`#${ticket?.id}`} color="primary" variant="filled" />
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
          
      <DialogContent sx={{ pt: 0.5 }}> 
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Sol Taraf - Temel Bilgiler */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid', borderColor: 'primary.main', pb: 1 }}>
                  Talep Bilgileri
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <InfoRow label="Firma" value={ticket.company_name} />
                  <InfoRow label="Program" value={ticket.module_name || modules.find((m: any) => m.id === ticket.module_id)?.name || 'Belirtilmemiş'} />               
                  
                  {[1, 2].includes(authUser?.role_id) && (
                    <InfoRow label="Öncelik" value={
                        <PriorityChip 
                        priorityId={ticket.priority_id}
                        priorities={priorities}
                        authUser={authUser}
                        />
                    } 
                        />
                  )}
                  
                  <InfoRow label="Durum" value={<StatusChip statusId={ticket.status_id} statuses={[]} />} />

                  {[1, 2].includes(authUser?.role_id) && (
                    <InfoRow label="Atanan Personel" value={ticket.assigned_to_name || 'Atanmadı'} />
                  )}

                  <InfoRow label="E-posta" value={ticket.email || 'Belirtilmemiş'} />
                </Box>
              </CardContent>
            </Card>

            {/* Ekler */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid', borderColor: 'success.main', pb: 1 }}>
                  📎 Ekler
                </Typography>

                {filesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Dosyalar yükleniyor...</Typography>
                  </Box>
                ) : ticketFiles.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 1 }}>
                    Bu talebe eklenmiş dosya bulunmuyor.
                  </Typography>
                ) : (
                  <List dense>
                    {ticketFiles.map((f: any) => {
                      const decodedName = decodeFileName(f.name);
                      return (
                        <ListItem
                          key={f.id}
                          secondaryAction={
                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                              <Tooltip title="Önizle">
                                <IconButton
                                  edge="end"
                                  onClick={() => onFilePreview(f)}
                                  size="small"
                                  sx={{ color: 'primary.main' }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="İndir">
                                <IconButton
                                  edge="end"
                                  onClick={() => onFileDownload(f)}
                                  size="small"
                                  sx={{ color: 'success.main' }}
                                >
                                  <FileDownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                          sx={{ 
                            py: 0.5,
                            pr: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 'none'
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Typography fontSize="20px">
                              {getFileIcon(f.name)}
                            </Typography>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Tooltip title={decodedName} arrow placement="top-start">
                                <Typography 
                                  variant="body2" 
                                  sx={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                  }}
                                >
                                  {decodedName}
                                </Typography>
                              </Tooltip>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(f.size)} 
                              </Typography>
                            }
                            sx={{
                              '& .MuiListItemText-primary': {
                                maxWidth: '200px'
                              }
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>

            {/* Zaman Bilgileri */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid', borderColor: 'secondary.main', pb: 1 }}>
                  ⏰ Zaman Bilgileri
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <InfoRow label="Oluşturulma" value={new Date(ticket.created_at).toLocaleString('tr-TR')} />
                  <InfoRow label="Son Güncelleme" value={ticket.updated_at ? new Date(ticket.updated_at).toLocaleString('tr-TR') : '-'} />
                  <InfoRow label="Kapanış Tarihi" value={ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleString('tr-TR') : ''} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sağ Taraf - Açıklama ve Geçmiş */}
          <Grid item xs={12} md={6}>
            {/* Talep Konusu */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid', borderColor: 'warning.main', pb: 1 }}>
                  📝 Talep Konusu
                </Typography>
                <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap', fontSize: '0.9rem'  }}>
                  {ticket.subject}
                </Typography>
              </CardContent>
            </Card>

            {/* Gelen Mail İçeriği */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid', borderColor: 'info.main', pb: 1 }}>
                  📧 Talep Detayları
                </Typography>
                <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap', fontSize: '0.9rem'  }}>
                  {ticket.mail_content || 'Mail içeriği bulunmuyor'}
                </Typography>
              </CardContent>
            </Card>

            {/* Sorun Açıklaması - Sadece Müşteri Değilse */}
            {authUser?.role !== 'customer' && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid', borderColor: 'error.main', pb: 1 }}>
                    🔍 Aksan Yazılım Sorunun Açıklaması
                  </Typography>
                  <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap', fontSize: '0.9rem'  }}>
                    {ticket.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Durum Geçmişi */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '2px solid', borderColor: 'primary.main', pb: 1 }}>
                  🔄 Durum Geçmişi
                </Typography>
                
                <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
                  {ticket.statusHistory && ticket.statusHistory.length > 0 ? (
                    (() => {
                      const filteredHistory = ticket.statusHistory.filter((history: any) => {
                        return !history.notes?.includes('içerik güncellendi') && 
                               !history.notes?.includes('İçerik güncellendi') &&
                               !history.notes?.includes('content updated') &&
                               !history.notes?.includes('konu güncellendi') &&
                               !history.notes?.includes('Konu güncellendi') &&
                               !history.notes?.includes('subject updated');
                      });

                      const sortedHistory = [...filteredHistory].sort((a, b) => 
                        new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
                      );

                      return sortedHistory.length > 0 ? (
                        sortedHistory.map((history: any, index: number) => (
                          <TimelineItem 
                            key={history.id}
                            history={history}
                            isLast={index === sortedHistory.length - 1}
                            authUser={authUser}
                          />
                        ))
                      ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          Henüz durum geçmişi bulunmuyor
                        </Typography>
                      );
                    })()
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Henüz durum geçmişi bulunmuyor
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>

        {canEdit && (
          <Button
            variant="outlined"
            onClick={() => {
              onClose();
              onEditTicketOpen(ticket);
            }}
          >
            Talep Düzenle
          </Button>
        )}

        {(authUser?.role === 'admin' || authUser?.role === 'support' || authUser?.role === 'customer') && (
          <Button
            variant="contained"
            onClick={() => {
              onClose();
              onEdit(ticket);
            }}
          >
            {authUser?.role === 'customer' ? 'Talep Kapat' : 'Talep Yönetme'}
          </Button>
        )}
      </DialogActions>
    </DialogWrapper>
  );
};